import api from './api';

const apiWithTags = api.enhanceEndpoints({
	addTagTypes: [
		'AgencyProfessionals',
		'AgencyProfessional',
		'AgencyContacts',
		'CredentialByType',
		'EnabledCredentials',
		'ShiftTypesByFacility',
		'CredentialsForShiftType'
	]
});

const agencyProfessionalsApi = apiWithTags.injectEndpoints({
	tagTypes: [
		'AgencyProfessionals',
		'AgencyProfessional',
		'CredentialByType',
		'EnabledCredentials',
		'ShiftTypesByFacility',
		'CredentialsForShiftType'
	],
	endpoints: (build) => ({
		agencyProfessionals: build.query({
			query: () => '/agencies/professionals',
			providesTags: ['AgencyProfessionals']
		}),
		agencyProfessional: build.query({
			query: (caregiverId) => `/agencies/professionals/${caregiverId}`,
			providesTags: ['AgencyProfessional']
		}),
		updateAgencyProfessional: build.mutation({
			query: ({ caregiverId, body }) => ({
				url: `/agencies/professionals/${caregiverId}`,
				method: 'PUT',
				body
			}),
			invalidatesTags: ['AgencyProfessional', 'AgencyProfessionals']
		}),
		updateAgencyProfessionalEnabledStatus: build.mutation({
			query: ({ caregiverId, body }) => ({
				url: `/agencies/professionals/${caregiverId}/update-enabled-status`,
				method: 'POST',
				body
			}),
			invalidatesTags: ['AgencyProfessional', 'AgencyProfessionals']
		}),
		submitAgencyProfessionalContactInfo: build.mutation({
			query: ({ body }) => ({
				url: `/agencies/professionals/contact-info`,
				method: 'POST',
				body
			}),
			invalidatesTags: ['AgencyProfessional', 'AgencyProfessionals']
		}),
		createAgencyProfessional: build.mutation({
			query: (body) => ({
				url: `/agencies/professionals/create`,
				method: 'POST',
				body
			}),
			invalidatesTags: ['AgencyProfessional', 'AgencyProfessionals']
		}),
		credentialByType: build.query({
			query: ({ caregiverId, credType, source }) =>
				source
					? `/agencies/professionals/${caregiverId}/credentials/${credType}?source=${source}`
					: `/agencies/professionals/${caregiverId}/credentials/${credType}`,
			providesTags: ['CredentialByType']
		}),
		uploadCredential: build.mutation({
			query: ({ caregiverId, credType, formData }) => ({
				url: `/agencies/professionals/${caregiverId}/credentials/${credType}`,
				method: 'POST',
				headers: { 'Content-Type': undefined },
				body: formData
			}),
			invalidatesTags: (result, error) =>
				error ? [] : ['CredentialByType', 'EnabledCredentials', 'CredentialsForShiftType']
		}),
		enabledCredentials: build.query({
			query: (caregiverId) => `/agencies/professionals/${caregiverId}/credentials`,
			providesTags: ['EnabledCredentials']
		}),
		shiftTypesByFacility: build.query({
			query: () => `/agencies/shift-types`,
			providesTags: ['ShiftTypesByFacility']
		}),
		credentialsForShiftType: build.query({
			query: ({ caregiverId, facilityId, shiftType, specialtyType }) => ({
				url: specialtyType
					? `/agencies/professionals/${caregiverId}/credential-requirements?facilityId=${facilityId}&shiftType=${encodeURIComponent(
							shiftType
					  )}&specialtyType=${encodeURIComponent(specialtyType)}`
					: `/agencies/professionals/${caregiverId}/credential-requirements?facilityId=${facilityId}&shiftType=${encodeURIComponent(
							shiftType
					  )}`
			}),
			providesTags: ['CredentialsForShiftType']
		}),
		setPrimaryLicenseGroup: build.mutation({
			query: ({caregiverId, groupId}) => ({
				url: `/agencies/professionals/${caregiverId}/professional-license/${groupId}`,
				method: 'POST',
				body: {}
			}),
			invalidatesTags: (result, error) =>
				error ? [] : ['CredentialByType', 'EnabledCredentials']
		})
	})
});

export const {
	useAgencyProfessionalsQuery,
	useAgencyProfessionalQuery,
	useUpdateAgencyProfessionalMutation,
	useUpdateAgencyProfessionalEnabledStatusMutation,
	useSubmitAgencyProfessionalContactInfoMutation,
	useCreateAgencyProfessionalMutation,
	useCredentialByTypeQuery,
	useUploadCredentialMutation,
	useEnabledCredentialsQuery,
	useShiftTypesByFacilityQuery,
	useCredentialsForShiftTypeQuery,
	useSetPrimaryLicenseGroupMutation
} = agencyProfessionalsApi;

const agencyContactsApi = apiWithTags.injectEndpoints({
	tagTypes: ['AgencyContacts'],
	endpoints: (build) => ({
		agencyContacts: build.query({
			query: () => '/agencies/contacts',
			providesTags: ['AgencyContacts']
		}),
		addAgencyContact: build.mutation({
			query: (body) => ({
				url: `/agencies/contacts`,
				method: 'POST',
				body
			}),
			invalidatesTags: ['AgencyContacts']
		}),
		updateAgencyContact: build.mutation({
			query: ({ id, body }) => ({
				url: `/agencies/contacts/${id}`,
				method: 'PUT',
				body
			}),
			invalidatesTags: ['AgencyContacts']
		})
	})
});

export const {
	useAgencyContactsQuery,
	useAddAgencyContactMutation,
	useUpdateAgencyContactMutation
} = agencyContactsApi;
